import {
  AbsoluteCenter,
  ControlBarWrapper,
  CustomControlButtonList,
  CustomControlWrapper,
  GradientX,
  ScreenPlayContainer,
} from '../styles';
import ControlBarFullScreen from './ControlBarFullScreen';
import { VideoJsPlayer } from 'video.js';
import ControlBarProgress from './ControlBarProgress';
import { useEffect, useRef, useState } from 'react';
import PlayControls from './PlayControls';
import RemindDuration from './RemindDuration';
import TitleBar from './TitleBar';
import ActionsBar from './ActionsBar';
import ControlBarNextPrevEpisode from './ControlBarNextPrevEpisode';
import ControlBarInfo from './ControlBarInfo';
import Watermark from './Watermark';
import LiveBadge from './LiveBadge';
import SeasonsAndEpisodes from './Episodes';
import Setting from './Setting/Setting';
import { Flex, Spinner } from '../../../styles';
import UsePlayerProps from '../../../Store/usePlayerProps';
import ControlBarVolumeController from './ControlBarVolumeController';
import ControlBarCaptionSwitch from './ControlBarCaptionSwitch';
import theme from '../../../theme';
import { updatePlayback } from '../PlayerServices';
import * as Sentry from '@sentry/nextjs';
import { useUser } from '../../../CacheProvider';
import { Sources, VideoContentData } from '../PlayerModels';

export default function PlayerCustomComponents({
  player,
  playingAd,
  handleErr,
  isShort,
  onShowInfo,
}: {
  player: VideoJsPlayer;
  playingAd: boolean;
  handleErr: () => void;
  isShort: boolean;
  onShowInfo?: () => void;
}) {
  const { user } = useUser();
  // const { tech } = useTech();
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [showControlBar, setShowControlBar] = useState<boolean>(false);
  const [activePanel, setActivePanel] = useState<boolean>(false);
  const timer = useRef<any>();
  const [FullScreen, setFullscreen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showCaptions, setShowCaptions] = useState<boolean>(false);
  const [showQualities, setShowQualities] = useState<boolean>(false);
  const [hasNoPreroll, setHasNoPreroll] = useState<boolean>(false);
  const [buffering, setBuffering] = useState<boolean>(false);
  const intervalID = useRef<any>(null);
  const { contentConfigs, contentData, sources } = playerGlobalState;
  const { videos } = sources as Sources;
  const { videoFileType } = playerGlobalState.contentData as VideoContentData;
  // const [isSeeking, setIsSeeking] = useState(false);
  // const [lastTap, setLastTap] = useState(0);
  // const tapTimeout = useRef<any>(null);
  //
  // useEffect(() => {
  //   player.on('seeking', () => {
  //     setIsSeeking(true);
  //   });
  //   player.on('seeked', () => {
  //     setIsSeeking(false);
  //   });
  //   return () => {
  //     player.off('seeking');
  //     player.off('seeked');
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  useEffect(() => {
    if (!player) return;

    if (!contentConfigs?.vast_url) {
      setHasNoPreroll(true);
    }

    // Handle Playback Request
    const intervalTimer = contentConfigs?.user_configuration?.playback_report_interval
      ? contentConfigs?.user_configuration?.playback_report_interval * 1000
      : 30000;

    const onNoPreroll = () => setHasNoPreroll(true);

    // Handle Captions Button
    // Subtitles only, which is what the caption panel lists: an iPhone adds text tracks of its
    // own to every HLS stream, and they would show the switch and panel with nothing in them.
    const onLoadedData = () => {
      if (!playingAd) {
        const tracks = player.textTracks()?.tracks_.filter((track) => track.kind === 'subtitles');
        if (tracks.length >= 1) setShowCaptions(true);
      }
    };

    const onLoadedMetadata = () => {
      if (!playingAd) {
        if (player.qualityLevels()?.levels_.length >= 1) setShowQualities(true);
      }
    };

    const onTimeUpdate = () => {
      // Shorts report once from the effect below rather than on this interval.
      if (!intervalID.current && !playingAd && videoFileType !== 'trailer' && !isShort) {
        intervalID.current = setInterval(() => {
          // An ad plays on this same player, and its position and URL are not a watch position.
          if (player.ads?.inAdBreak?.()) return;
          if (player?.currentTime() > 0)
            updatePlayback(contentData?.id, contentData?.videoFileId, player.currentTime(), player.src());
        }, intervalTimer);
      }
    };

    const stopReporting = () => {
      clearInterval(intervalID.current);
      intervalID.current = null;
    };

    // Handle Player Errors
    const onError = () => {
      // if (err.code === 3) {
      //   setDecodeErr(true);
      //   if (player.currentTime() < 5) {
      //     document.cookie = 'filmNetPlayerTechConfig=mp4; path=/; max-age=31536000';
      //     window.location.reload();
      //   }
      // }
      if (videos.find((item) => item.src === player.src())) {
        handleErr();
      }
      stopReporting();
      const error = player.error() ? player.error() : {};
      error.source = JSON.stringify(videos);
      Sentry.withScope((scope) => {
        scope.setUser({ id: user?.id, username: String(user?.msisdn) });
        scope.setTag('Tech', 'hls');
        scope.setTag('ErrorCode', error.code);
        scope.setTag('Player', 'video-js');
        scope.setTag('Edge', extractEdgeName(player.src()));
        Sentry.captureException(error);
      });
    };

    // The pre-roll is also over when it doesn't come in time or is skipped, and contrib-ads then
    // fires neither 'nopreroll' nor, with no ad played, 'vast.complete'.
    player.on(['nopreroll', 'vast.complete', 'adtimeout', 'adskip'], onNoPreroll);
    player.on('loadeddata', onLoadedData);
    player.on('loadedmetadata', onLoadedMetadata);
    player.on('timeupdate', onTimeUpdate);
    player.on('pause', stopReporting);
    // video.js emits `ended`, not `end`: the old spelling never fired, so the report
    // interval kept ticking after the video finished until pause or unmount.
    player.on('ended', stopReporting);
    player.on('error', onError);

    // Removed by reference. A bare player.off('timeupdate') would also drop the listeners
    // ControlBarProgress, NextEpisode and SkipIntro register on this same player.
    return () => {
      stopReporting();
      player.off(['nopreroll', 'vast.complete', 'adtimeout', 'adskip'], onNoPreroll);
      player.off('loadeddata', onLoadedData);
      player.off('loadedmetadata', onLoadedMetadata);
      player.off('timeupdate', onTimeUpdate);
      player.off('pause', stopReporting);
      player.off('ended', stopReporting);
      player.off('error', onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  useEffect(() => {
    handleMobileBreakpoint();
    window.addEventListener('resize', handleMobileBreakpoint);
    document.addEventListener('touchmove', handleMouseMove);
    if (IS_IOS) {
      document.addEventListener('click', handleMouseMove);
    } else {
      document.addEventListener('keydown', handleMouseMove);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mousedown', handleMouseMove);
    }

    return () => {
      clearTimeout(timer.current);
      timer.current = null;
      window.removeEventListener('resize', handleMobileBreakpoint);
      document.removeEventListener('touchmove', handleMouseMove);
      if (IS_IOS) {
        document.removeEventListener('click', handleMouseMove);
      } else {
        document.removeEventListener('keydown', handleMouseMove);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mousedown', handleMouseMove);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A short reports its playback once, when its episode starts playing. The shorts page mounts
  // a player per episode, so a one-shot listener needs no bookkeeping of its own; an ad of its
  // own fires `adplaying`, so this only ever sees the content start.
  useEffect(() => {
    if (!isShort) return;
    const report = () => {
      updatePlayback(contentData?.id, contentData?.videoFileId, player.currentTime(), player.src(), true);
    };
    player.one('playing', report);
    return () => player.off('playing', report);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stands in for video.js's own spinner, which is hidden for shorts because it anchors to
  // the viewport rather than the player box.
  useEffect(() => {
    if (!isShort) return;
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);

    player.on('waiting', onWaiting);
    player.on('playing', onPlaying);

    return () => {
      player.off('waiting', onWaiting);
      player.off('playing', onPlaying);
    };
  }, [player, isShort]);

  const extractEdgeName = (url: string) => {
    const match = url.match(/\bgp\d+\b/);
    return match ? match[0] : 'arvan';
  };

  const handleMouseMove = () => {
    const playerWrapper = document.getElementById('custom-player-wrapper');
    setShowControlBar(true);
    if (playerWrapper) playerWrapper.style.cursor = 'default';
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (playerWrapper) playerWrapper.style.cursor = 'none';
      setShowControlBar(false);
    }, 5000);
  };

  const handleScreenClick = () => {
    // Shorts toggle on tap at every size; the full-page player keeps controls-only on touch.
    if (activePanel || (isMobile && !isShort)) return;
    if (player.paused()) {
      player.play();
    } else {
      player.pause();
    }
  };

  const handleMobileBreakpoint = () => {
    setIsMobile(Number(theme.breakpoints.md) > Number(window.innerWidth));
  };

  // const handleSkip = (skipTime) => {
  //   if (player && !isSeeking) {
  //     const currentTime = player.currentTime();
  //     player.currentTime(currentTime + skipTime);
  //   }
  // };
  //
  // const handleTouchEnd = (skipTime) => {
  //   const currentTime = new Date().getTime();
  //   const tapGap = currentTime - lastTap;
  //
  //   // Time threshold for a double tap (in milliseconds)
  //   const doubleTapThreshold = 300;
  //
  //   if (tapGap < doubleTapThreshold && tapGap > 0) {
  //     // If second tap happens within the threshold time, trigger double tap
  //     clearTimeout(tapTimeout.current);
  //     if (player && !isSeeking) {
  //       const currentTime = player.currentTime();
  //       player.currentTime(currentTime + skipTime);
  //     }
  //   } else {
  //     // Otherwise, set timeout to clear after single tap (optional for long press or single-tap events)
  //     tapTimeout.current = setTimeout(() => {
  //       // Add single-tap behavior if needed (e.g., triggering click)
  //       // console.log('Single tap detected');
  //     }, doubleTapThreshold);
  //   }
  //
  //   setLastTap(currentTime);
  // };
  const handleFullScreen = () => {
    setFullscreen(!FullScreen);
    if (player.paused) {
      player.play();
    }
  };

  return (
    <div style={playingAd ? { display: 'none' } : { display: 'block' }}>
      <ScreenPlayContainer onClick={handleScreenClick} onDoubleClick={isShort ? undefined : handleFullScreen}></ScreenPlayContainer>
      {/* <ScreenRightContainer onTouchEnd={() => handleTouchEnd(+15)} onTouchStart={handleScreenClick}></ScreenRightContainer> */}
      {/* <ScreenLeftContainer onTouchEnd={() => handleTouchEnd(-15)} onTouchStart={handleScreenClick}></ScreenLeftContainer> */}
      {isShort && buffering && (
        <AbsoluteCenter>
          <Spinner block size={isMobile ? 'medium' : 'big'} color="primary" />
        </AbsoluteCenter>
      )}
      {!showControlBar && !activePanel && <Watermark />}
      {!isShort && hasNoPreroll && playerGlobalState.contentConfigs?.playback_type !== 'live_stream' &&
        <ActionsBar player={player}/>}
      <CustomControlWrapper show={showControlBar || activePanel}>
        {playerGlobalState.contentConfigs?.playback_type !== 'live_stream' && <RemindDuration player={player}/>}
        <PlayControls player={player} isShort={isShort}/>
        <TitleBar isShort={isShort}/>
        <GradientX/>
        <ControlBarWrapper>
          <ControlBarProgress player={player} playingAd={false}/>
          <CustomControlButtonList>
            <Flex>
              {playerGlobalState.contentConfigs && playerGlobalState.contentData?.content?.type === 'episode' &&
                <ControlBarNextPrevEpisode/>}
              {!!playerGlobalState.contentData?.seasons?.length &&
                <SeasonsAndEpisodes emitActivePanel={(status) => setActivePanel(status)}/>}
              <ControlBarVolumeController player={player}/>
              {onShowInfo && <ControlBarInfo onClick={onShowInfo}/>}
              {playerGlobalState.contentConfigs?.playback_type === 'live_stream' && <LiveBadge player={player}/>}
            </Flex>
            <Flex>
              {/* Shorts have no playback settings: the caption toggle, and the panel behind it
                  with quality, audio track, speed and auto-next, are all decisions the format
                  makes for the viewer. */}
              {!isShort && showCaptions && <ControlBarCaptionSwitch player={player}/>}
              {!isShort && (
                <Setting
                  player={player}
                  showCaptions={showCaptions}
                  changeCaptionPosition={showControlBar}
                  showQualities={showQualities}
                  emitActivePanel={(status) => setActivePanel(status)}
                />
              )}
              {!isShort &&
                <ControlBarFullScreen player={player} fullScreenState={FullScreen} hideControls={() => {
                  setShowControlBar(false);
                  clearTimeout(timer.current);
                }}/>}
            </Flex>
          </CustomControlButtonList>
        </ControlBarWrapper>
      </CustomControlWrapper>
    </div>
  );
}
// const ScreenRightContainer = styled.div(() => {
//   return {
//     position: 'absolute',
//     right: 0,
//     top: '25%',
//     width: '30%',
//     height: '50%',
//     background: 'transparent',
//     zIndex: 1,
//   };
// });

// const ScreenLeftContainer = styled.div(() => {
//   return {
//     position: 'absolute',
//     left: 0,
//     top: '25%',
//     width: '30%',
//     height: '50%',
//     background: 'transparent',
//     zIndex: 1,
//   };
// });
